import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { saveAs } from "file-saver";
import React from "react";
import { useDispatch } from "react-redux";
import {
  createGoogleDocsSpreadsheet,
  exportTable,
  getIntegrations,
} from "../../../../docuclipper/api";
import { Integration } from "../../../../docuclipper/DocuclipperTypes";
import { logErrorToGA } from "../../../../docuclipper/Logger";
import { createAlert } from "../../../../redux/reducers/Alerts";
import { LoadingTrackedButton } from "../../../Docuclipper/TrackedButton";

type Props = {
  text: string;
};
export const ExportTable: React.SFC<Props> = ({ text }) => {
  // integrations
  const [integrations, setIntegrations] = React.useState<Integration[]>([]);

  // exports
  const [csvLoading, setCsvLoading] = React.useState(false);
  const [xlsxLoading, setXlsxLoading] = React.useState(false);
  const [gdocsLoading, setGdocsLoading] = React.useState(false);

  const dispatch = useDispatch();

  React.useEffect(() => {
    getIntegrations()
      .then((integrations) => {
        setIntegrations(integrations);
      })
      .catch((err) => {});
  }, []);

  let hasGdocsIntegration = false;
  for (const i of integrations) {
    if (i.scope === "https://www.googleapis.com/auth/spreadsheets") {
      hasGdocsIntegration = true;
      break;
    }
  }

  const exportTableFn = async (format: "csv" | "xlsx" | "gdoc") => {
    if (format === "csv") {
      try {
        setCsvLoading(true);
        const rsp = await exportTable(text, "Csv");
        saveAs(rsp, `file.${format}`);
        dispatch(
          createAlert({
            type: "success",
            message: [
              `Table exported successfully. Check your Downloads folder.`,
            ],
            timeout: 0,
          })
        );
        setCsvLoading(false);
      } catch (err) {
        logErrorToGA(err);
        setCsvLoading(false);
        dispatch(
          createAlert({
            message: err.message || "Error exporting table",
            timeout: 0,
            type: "error",
          })
        );
      }
    } else if (format === "xlsx") {
      try {
        setXlsxLoading(true);
        const rsp = await exportTable(text, "Xlsx");
        saveAs(rsp, `file.${format}`);
        createAlert({
          type: "success",
          message: [
            `Table exported successfully. Check your Downloads folder.`,
          ],
          timeout: 0,
        });
        setXlsxLoading(false);
      } catch (err) {
        logErrorToGA(err);
        setXlsxLoading(false);
        dispatch(
          createAlert({
            message: err.message || "Error exporting table",
            timeout: 0,
            type: "error",
          })
        );
      }
    } else if (format === "gdoc") {
      try {
        setGdocsLoading(true);
        const rsp = await createGoogleDocsSpreadsheet(text);
        if (rsp.data && rsp.data.spreadsheetUrl) {
          dispatch(
            createAlert({
              type: "success",
              message: [
                <a
                  rel="noopener noreferrer"
                  key={"1"}
                  target="_blank"
                  href={`${rsp.data.spreadsheetUrl}`}
                >
                  Google Docs Spreadsheet
                </a>,
                " created successfully.",
              ],
              timeout: 0,
            })
          );
        }
        setGdocsLoading(false);
      } catch (err) {
        logErrorToGA(err);
        setGdocsLoading(false);
        dispatch(
          createAlert({
            message: err.message || "Error exporting table",
            timeout: 0,
            type: "error",
          })
        );
      }
    }
  };

  return (
    <>
      <LoadingTrackedButton
        onClick={() => exportTableFn("xlsx")}
        isLoading={xlsxLoading}
        variant="link"
        className="btn--save-xlsx"
      >
        <FontAwesomeIcon icon="file-excel" />
        <span className="ml-1">XLSX</span>
      </LoadingTrackedButton>
      <LoadingTrackedButton
        onClick={() => exportTableFn("csv")}
        isLoading={csvLoading}
        variant="link"
        className="btn--save-csv"
      >
        <FontAwesomeIcon icon="file-csv" />
        <span className="ml-1">CSV</span>
      </LoadingTrackedButton>
      {hasGdocsIntegration && (
        <LoadingTrackedButton
          onClick={() => exportTableFn("gdoc")}
          isLoading={gdocsLoading}
          variant="link"
          className="btn--save-gdoc"
        >
          <FontAwesomeIcon icon="google" />
          <span className="ml-1">Google doc</span>
        </LoadingTrackedButton>
      )}
    </>
  );
};
export default ExportTable;
